import { useMemo } from 'react';
import type { BreakdownSubItem, BreakdownSubItemInput } from '@jobos/shared';
import { useCarpetCatalog } from '@/hooks/useCarpetCatalog';
import { type SubItemForm } from '../breakdown/subItemTypes';
import { CarpetEditor } from './CarpetEditor';

interface CarpetLineItemBodyProps {
  subItems: SubItemForm[];
  onSubItemsChange: (next: SubItemForm[]) => void;
  readOnly?: boolean;
}

function toBreakdownSubItems(forms: SubItemForm[]): BreakdownSubItem[] {
  return forms.map(
    (f) =>
      ({
        id: f.id,
        lineItemId: '',
        type: f.type,
        hours: 'hours' in f ? f.hours : null,
        rate: 'rate' in f ? f.rate : null,
        amount: 'amount' in f ? f.amount : null,
        note: f.note ?? null,
        sortOrder: 0,
        createdAt: '',
        updatedAt: '',
      }) as unknown as BreakdownSubItem,
  );
}

function toSubItemForms(inputs: BreakdownSubItemInput[]): SubItemForm[] {
  return inputs.map((s): SubItemForm => {
    const base = { id: crypto.randomUUID(), type: s.type, ...(s.note ? { note: s.note } : {}) };
    if (s.amount != null) {
      return { ...base, amount: Number(s.amount) };
    }
    return { ...base, hours: Number(s.hours ?? 0), rate: Number(s.rate ?? 0) };
  });
}

export function CarpetLineItemBody({
  subItems,
  onSubItemsChange,
  readOnly,
}: CarpetLineItemBodyProps) {
  const catalogQuery = useCarpetCatalog();
  const initial = useMemo(() => toBreakdownSubItems(subItems), [subItems]);

  if (catalogQuery.isLoading) {
    return <div className="p-4 text-sm text-gray-500">Loading carpet catalog…</div>;
  }
  if (catalogQuery.isError || !catalogQuery.data) {
    return (
      <div className="p-4 text-sm text-red-600">Failed to load carpet catalog.</div>
    );
  }

  if (readOnly) {
    // Read-only: show the editor with no change handler. CarpetEditor doesn't
    // expose a readOnly knob itself, so suppress emissions at the boundary.
    return (
      <div className="pointer-events-none opacity-90">
        <CarpetEditor
          catalog={catalogQuery.data}
          initialSubItems={initial}
          onChange={() => {}}
        />
      </div>
    );
  }

  return (
    <CarpetEditor
      catalog={catalogQuery.data}
      initialSubItems={initial}
      onChange={(next) => onSubItemsChange(toSubItemForms(next))}
    />
  );
}
